const HERO_IMAGE =
  'https://images.unsplash.com/photo-1554068865-24cecd4e34b8?auto=format&fit=crop&w=1600&q=80';

/**
 * Featured guide: free and open public tennis courts near Powell, Dublin,
 * Polaris corridor, Lewis Center, and Delaware, Ohio.
 */
export const OHIO_OPEN_TENNIS_COURTS_POST = {
  id: 'featured-ohio-open-tennis-courts',
  slug: 'open-tennis-courts-powell-dublin-polaris-delaware-ohio',
  title: 'Free Tennis Courts Near Powell, Dublin, Polaris & Delaware (Ohio)',
  excerpt:
    'Where to play pickup tennis without a club membership—city parks, township courts, and neighborhood hard courts from Powell to Delaware. First-come spots, lighted courts, and what to know before you drive.',
  publishedAt: '2026-07-22T21:00:00.000Z',
  coverImageUrl: HERO_IMAGE,
  authorName: 'Daily Sports Life',
  clubName: 'Ohio Court Guide',
  blogCategory: 'club-news',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Guide overview',
      items: [
        { value: '4 areas', label: 'Powell · Dublin · Polaris · Delaware' },
        { value: '30+', label: 'Listed court sites' },
        { value: 'First come', label: 'Most public parks' },
        { value: 'Check nets', label: 'Bring balls & patience' },
      ],
    },
    {
      type: 'panel',
      title: 'How to read this guide',
      paragraphs: [
        'This list is for casual players, families, and rec groups looking for hard courts in northern Delaware County and northwest Franklin County—no country-club membership required.',
        '“Free & open” means you can walk on and hit when a court is empty. Some sites allow optional paid reservations for guaranteed time; school courts are not public unless posted.',
        'Many Dublin courts share lines with pickleball. Yield to permitted lessons, league play, and anyone already on court. Bring your own balls; nets are usually permanent but condition varies by park.',
      ],
      chips: ['Pickup friendly', 'Hard courts', 'Ohio 43015–43065', 'Updated July 2026'],
    },
    {
      type: 'table',
      title: 'Powell — Liberty Township & City of Powell',
      intro:
        'Powell’s best public tennis inventory is at Liberty Park (Liberty Township) and Arbor Ridge Park (City of Powell). Both are hard courts, generally free on a first-come basis; Liberty Township also rents courts by the hour.',
      headers: ['Courts / park', 'Address', 'Access', 'Notes'],
      rows: [
        [
          'Liberty Park — tennis complex',
          '2845 Home Rd, Powell, OH 43065',
          'Free & open · Optional reserve ($15/hr)',
          'Liberty Township’s largest park—multiple hard courts, restrooms, trails, playgrounds. Open 8 a.m.–dusk (township rules). Hitting wall reported nearby.',
        ],
        [
          'Arbor Ridge Park — 4 courts',
          '500 Bennett Pkwy, Powell, OH 43065',
          'Free & open when empty',
          'Fenced regulation courts in a neighborhood park. City of Powell lists courts on RecDesk; popular for doubles and junior practice.',
        ],
        [
          'Liberty Township Park (County Rd 124)',
          '2507 County Rd 124, Powell, OH 43065',
          'Free & open',
          'Four hard courts with lights and backboard reported—good evening option west of Liberty Park.',
        ],
      ],
    },
    {
      type: 'table',
      title: 'Dublin — City parks (largest inventory)',
      intro:
        'Dublin has the densest free public tennis network in this guide. Coffman Park is the flagship cluster; Avery Park and neighborhood parks offer quieter two-court options. Many courts now include pickleball lines—check before you claim a court for singles.',
      headers: ['Courts / park', 'Address', 'Access', 'Notes'],
      rows: [
        [
          'Coffman Park tennis courts',
          '5600 Coffman Rd, Dublin, OH 43017',
          'Free & open · Dawn–11 p.m.',
          'Largest public cluster in Dublin (up to 8 courts reported). Near DCRC, shelters, and playground. Busiest on weeknights—arrive early or rotate.',
        ],
        [
          'Avery Park',
          '7401 Avery Rd, Dublin, OH 43016',
          'Free & open',
          'Two tennis courts in Dublin’s main sports park (soccer fields nearby). Shared pickleball lines per city guidance.',
        ],
        [
          'Donegal Cliffs Park',
          '4450 Dunleary Dr, Dublin, OH 43017',
          'Free & open',
          'Two neighborhood courts with permanent nets—quiet weekday option near Tuttle Crossing.',
        ],
        [
          'Wellington Park',
          '7467 Coventry Woods Dr N, Dublin, OH 43017',
          'Free & open',
          'Two courts in a residential pocket park north of I-270.',
        ],
        [
          'Balgriffin Park',
          '5764 Parkside Xing, Dublin, OH 43016',
          'Free & open',
          'Two courts near Coffman HS corridor—handy for east Dublin players.',
        ],
        [
          'Woods of Brighton / Brighton Park',
          '5613 Brighton Hill Ln & nearby, Dublin, OH 43016',
          'Free & open',
          'Two-court neighborhood sites; city lists both for tennis/pickleball shared use.',
        ],
        [
          'Llewellyn Farms Park',
          '4696 Tuttle Rd, Dublin, OH 43017',
          'Free & open',
          'Small two-court park off Tuttle—good for quick hit-and-run sessions.',
        ],
        [
          'Monterey Park',
          'Dublin, OH 43016 (neighborhood park)',
          'Free & open',
          'Compact neighborhood courts—verify availability on arrival; popular with local families.',
        ],
      ],
    },
    {
      type: 'table',
      title: 'Polaris & Lewis Center corridor',
      intro:
        'There are no dedicated public tennis courts inside the Polaris retail district itself. For free play near Polaris Fashion Place and I-71, drive to Antrim Park or cross into Powell’s Liberty/Arbor Ridge courts (10–15 minutes). Lewis Center township parks focus on soccer and pickleball—not tennis.',
      headers: ['Courts / park', 'Address', 'Access', 'Notes'],
      rows: [
        [
          'Antrim Park',
          '5800 Olentangy River Rd, Columbus, OH 43085',
          'Free & open',
          'Four outdoor hard courts—closest major free public cluster to Polaris. Lighted; restrooms and river trail nearby. Columbus Parks property.',
        ],
        [
          'Liberty Park (from Polaris north)',
          '2845 Home Rd, Powell, OH 43065',
          'Free & open · Optional reserve',
          '~10 min north of Polaris—see Powell section. Best bet when Antrim is full.',
        ],
        [
          'Arbor Ridge Park (from Polaris north)',
          '500 Bennett Pkwy, Powell, OH 43065',
          'Free & open',
          'Neighborhood alternative with four fenced courts; less traffic than Antrim on weekends.',
        ],
        [
          'North Road Park',
          '5864 North Rd, Lewis Center, OH 43035',
          'Pickleball only',
          'Excellent pickleball courts but no dedicated tennis—do not set up tennis lines here.',
        ],
        [
          'Olentangy school campuses',
          '675 Lewis Center Rd & 2840 E Orange Rd',
          'School permit only',
          'Multiple courts on campus—reserved for school/club use unless you have permission.',
        ],
      ],
    },
    {
      type: 'table',
      title: 'Delaware — City & township parks',
      intro:
        'Delaware’s anchor is Mingo Park—four lighted courts, restrooms, and a hitting wall. Smith Park adds a larger cluster on the east side; smaller neighborhood parks fill in one- and two-court options.',
      headers: ['Courts / park', 'Address', 'Access', 'Notes'],
      rows: [
        [
          'Mingo Park tennis courts',
          '500 E Lincoln Ave, Delaware, OH 43015',
          'Free & open',
          'Four lighted hard courts—city’s primary public tennis hub. Pool, skate park, and rec center on site. Best evening option in Delaware.',
        ],
        [
          'Smith Park',
          '1302 Troy Rd, Delaware, OH 43015',
          'Free & open · Check league use',
          'Up to eight courts reported—popular for clinics and league windows. Confirm open courts before driving out.',
        ],
        [
          'Wingate Community Park',
          '1367 Wingate Dr, Delaware, OH 43015',
          'Free & open',
          'Two neighborhood courts on the west side—quieter than Mingo.',
        ],
        [
          'Concord Township Park',
          '6385 Home Rd, Delaware, OH 43015',
          'Free & open',
          'Four courts with permanent nets—handy for Powell/Delaware border players.',
        ],
        [
          'Carson Farms Park',
          '1536 Plowington Dr, Delaware, OH 43015',
          'Free & open',
          'Single neighborhood court—good for parent-child rally practice.',
        ],
        [
          'Hayes Colony Park',
          '501 Harding Ln, Delaware, OH 43015',
          'Free & open',
          'Two courts in a residential park south of Hayes HS.',
        ],
        [
          'Delaware Hayes HS courts',
          '289 Euclid Ave, Delaware, OH 43015',
          'School permit only',
          'Large school complex—do not assume public access during school hours or team seasons.',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'Quick tips before you play',
      list: [
        'Courts are first-come at most parks—limit yourself to one hour if others are waiting (common courtesy).',
        'Many Dublin courts share pickleball lines; rotate with pickleball players rather than taping new lines.',
        'Spring/winter cracks and fall leaf debris are common—wear court shoes and watch for slippery leaves.',
        'Lighted courts (Mingo, Antrim, some Powell sites) extend play past sunset; non-lighted parks close at dusk.',
        'School campuses (Olentangy, Hayes, Coffman HS) are not reliable public options without permission.',
      ],
    },
    {
      type: 'panel',
      title: 'Before you drive — 60-second checklist',
      orderedList: [
        'Pick the closest park with enough courts for your group size (singles = 1, doubles = 1, two matches = 2+).',
        'Check weather and wind—open parks have no indoor backup.',
        'Pack balls, water, towels, and overgrips; public courts rarely have ball machines or pro shops.',
        'If meeting friends, share the parking lot name (Coffman and Mingo lots fill on nice evenings).',
        'Confirm park hours—Dublin Coffman allows play until 11 p.m.; most township parks close at dusk.',
      ],
      callout:
        'Court counts and hours change seasonally. Use the official park links below before planning a new weekly hitting group.',
    },
  ],
  referenceLinks: [
    {
      label: 'City of Dublin — pickleball & shared tennis court list',
      url: 'https://dublinohiousa.gov/pickleball/',
    },
    {
      label: 'City of Powell — Arbor Ridge courts (RecDesk)',
      url: 'https://powell.recdesk.com/Community/Facility/Detail?facilityId=16',
    },
    {
      label: 'Liberty Township — Liberty Park courts (RecDesk)',
      url: 'https://libertypark.recdesk.com/Community/Facility/Detail?facilityId=16',
    },
    {
      label: 'City of Delaware — Mingo Park',
      url: 'https://www.delawareohio.net/Home/Components/FacilityDirectory/FacilityDirectory/68/146',
    },
    {
      label: 'Columbus Parks — Antrim Park',
      url: 'https://www.columbus.gov/recreationandparks/parks/Antrim-Park',
    },
    {
      label: 'Orange Township parks (Lewis Center)',
      url: 'https://www.orangetwp.org/townshipparks/',
    },
  ],
};
